import { useRef, useState } from "react";
import { Image as ImageIcon, X, Loader2 } from "lucide-react";
import { compressImage } from "../../lib/api";

export default function ImageUploader({
    value,
    onChange,
    testIdPrefix = "image",
}) {
    const fileInputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [urlMode, setUrlMode] = useState(false);
    const [urlInput, setUrlInput] = useState("");

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            alert("Por favor selecciona una imagen.");
            return;
        }
        setBusy(true);
        try {
            // Compress + resize on the client to keep base64 payload small
            // (long edge 1280px, jpeg 78%) — improves both upload speed and
            // public page load times.
            const base64 = await compressImage(file, 1280, 0.78);
            onChange(base64);
        } catch (err) {
            console.error(err);
            alert("No se pudo procesar la imagen. Prueba con otra foto.");
        } finally {
            setBusy(false);
        }
    };

    const onPick = (e) => handleFile(e.target.files?.[0]);

    const onDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        handleFile(file);
    };

    const onApplyUrl = () => {
        if (!urlInput.trim()) return;
        onChange(urlInput.trim());
        setUrlInput("");
        setUrlMode(false);
    };

    return (
        <div data-testid={`${testIdPrefix}-uploader`}>
            {value ? (
                <div className="relative group">
                    <img
                        src={value}
                        alt="Vista previa"
                        className="w-full aspect-[4/5] object-cover bg-[#f0ece4]"
                        data-testid={`${testIdPrefix}-preview`}
                    />
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        className="absolute top-2 right-2 w-8 h-8 bg-white/90 hover:bg-white text-[#1C1A18] flex items-center justify-center shadow"
                        data-testid={`${testIdPrefix}-clear`}
                        aria-label="Quitar imagen"
                    >
                        <X size={16} />
                    </button>
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute bottom-2 left-2 right-2 bg-[#1C1A18]/85 hover:bg-[#1C1A18] text-white text-[10px] tracking-[0.32em] uppercase py-2"
                        data-testid={`${testIdPrefix}-replace`}
                    >
                        Reemplazar
                    </button>
                </div>
            ) : (
                <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={onDrop}
                    className="border border-dashed border-[#c9c3b8] aspect-[4/5] flex flex-col items-center justify-center text-center p-4 bg-[#fcfaf6]"
                >
                    {busy ? (
                        <Loader2 className="animate-spin text-[#C4A882]" size={28} />
                    ) : (
                        <>
                            <ImageIcon size={32} className="text-[#C4A882] mb-3" strokeWidth={1.2} />
                            <p className="text-sm text-[#6b665f] font-light mb-4">
                                Selecciona una imagen desde tu galería, foto o pega una URL.
                            </p>
                            <div className="flex flex-col items-center gap-2 w-full">
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    data-testid={`${testIdPrefix}-pick-file`}
                                    className="w-full max-w-[220px] px-4 py-2.5 btn-gold text-[10px] uppercase"
                                >
                                    Subir desde dispositivo
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setUrlMode((v) => !v)}
                                    data-testid={`${testIdPrefix}-toggle-url`}
                                    className="text-[10px] tracking-[0.28em] uppercase text-[#6b665f] hover:text-[#1C1A18] link-underline mt-2"
                                >
                                    O pegar URL
                                </button>
                            </div>
                            {urlMode && (
                                <div className="mt-4 w-full flex gap-2">
                                    <input
                                        type="url"
                                        value={urlInput}
                                        onChange={(e) => setUrlInput(e.target.value)}
                                        placeholder="https://…"
                                        className="field flex-1 text-xs"
                                        data-testid={`${testIdPrefix}-url-input`}
                                    />
                                    <button
                                        type="button"
                                        onClick={onApplyUrl}
                                        data-testid={`${testIdPrefix}-url-apply`}
                                        className="px-3 py-2 bg-[#1C1A18] text-white text-[10px] uppercase"
                                    >
                                        OK
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onPick}
                className="hidden"
                data-testid={`${testIdPrefix}-file-input`}
            />
        </div>
    );
}
